import { create } from 'zustand'

interface SelectionState {
  selectedIds: Set<string>
  lastSelectedId: string | null
  isSelectionMode: boolean

  toggle: (id: string) => void
  rangeSelect: (id: string, orderedIds: string[]) => void
  selectAll: (ids: string[]) => void
  clearSelection: () => void
  setSelectionMode: (on: boolean) => void
}

export const useSelectionStore = create<SelectionState>()((set) => ({
  selectedIds: new Set<string>(),
  lastSelectedId: null,
  isSelectionMode: false,

  toggle: (id) =>
    set((s) => {
      const next = new Set(s.selectedIds)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return {
        selectedIds: next,
        lastSelectedId: id,
        isSelectionMode: s.isSelectionMode,
      }
    }),

  rangeSelect: (id, orderedIds) =>
    set((s) => {
      const anchor = s.lastSelectedId
      if (!anchor || !orderedIds.includes(anchor)) {
        const next = new Set(s.selectedIds)
        next.add(id)
        return { selectedIds: next, lastSelectedId: id }
      }
      const anchorIdx = orderedIds.indexOf(anchor)
      const targetIdx = orderedIds.indexOf(id)
      if (targetIdx === -1) return s

      const start = Math.min(anchorIdx, targetIdx)
      const end = Math.max(anchorIdx, targetIdx)
      const next = new Set(s.selectedIds)
      for (let i = start; i <= end; i++) {
        next.add(orderedIds[i])
      }
      return { selectedIds: next, lastSelectedId: id }
    }),

  selectAll: (ids) =>
    set({
      selectedIds: new Set(ids),
      lastSelectedId: ids.length > 0 ? ids[ids.length - 1] : null,
    }),

  clearSelection: () =>
    set({
      selectedIds: new Set<string>(),
      lastSelectedId: null,
      isSelectionMode: false,
    }),

  setSelectionMode: (on) => set({ isSelectionMode: on }),
}))
